﻿import { Controller } from "@hotwired/stimulus";
import Flickity from "flickity";
import "flickity-as-nav-for";
import pDebounce from "p-debounce";

export default class extends Controller {
    static get values() {
        return {
            customAmountText: String,
            calculatePointsForCustomVariantAmountUrl: String,
            confirmTitle: String,
            confirmMessage: String,
            currencySuffix: String
        }
    }

    static get targets() {
        return ["imageCarousel", "imageCarouselNav", "price", "points", "customVariant", "customVariantAmount", "CustomVariantAmount_Submit", "variantSelect", "giftRecipient", "giftOther"];
    }

    initialize() {
        this.calculatePointsForCustomVariantAmount = pDebounce(this.calculatePointsForCustomVariantAmount, 250);
    }

    imageCarouselTargetConnected() {
        this.imageCarouselTarget.flickity = new Flickity(
            this.imageCarouselTarget,
            {
                cellAlign: "left",
                contain: true,
                pageDots: false,
                // arrowShape: "M98.4,86c0,6.6-5.4,12-12,12h-72c-6.6,0-12-5.4-12-12V14c0-6.6,5.4-12,12-12h72c6.6,0,12,5.4,12,12V86z M71.4,47H36.7 l12.9-12.9c1.2-1.2,1.2-3.1,0-4.2s-3.1-1.2-4.2,0l-18,18c-1.2,1.2-1.2,3.1,0,4.2c0,0,0,0,0,0l18,18c1.2,1.2,3.1,1.2,4.2,0c1.2-1.2,1.2-3.1,0-4.2L36.7,53h34.8c1.7,0,3-1.3,3-3C74.4,48.4,73.1,47,71.4,47z"
            }
        );
    }

    imageCarouselNavTargetConnected() {
        this.imageCarouselNavTarget.flickity = new Flickity(
            this.imageCarouselNavTarget,
            {
                asNavFor: this.imageCarouselTarget,
                cellAlign: "left",
                contain: true,
                prevNextButtons: false,
                pageDots: false
            }
        );
    }

    displaySelectedVariant({ target }) {
        const variantId = target.options[target.selectedIndex].dataset.variantId;
        const variantImage = this.imageCarouselTarget.querySelector(`[data-variant-id="${variantId}"]`);
        const imageIndex = variantImage ? Array.prototype.indexOf.call(variantImage.parentNode.children, variantImage) : 0;
        this.imageCarouselTarget.flickity.select(imageIndex, false, true);

        if (this.hasImageCarouselNavTarget) {
            this.imageCarouselTarget.flickity.select(imageIndex, false, true);
        }

        const price = target.options[target.selectedIndex].dataset.price;

        if (this.hasPriceTarget && price) {
            this.priceTarget.innerHTML = price;
        }

        const points = target.options[target.selectedIndex].dataset.points;
        const preSalePoints = target.options[target.selectedIndex].dataset.preSalePoints;
        this.pointsTarget.innerHTML = preSalePoints ?
            `
            <span class="text-decoration-line-through text-secondary">${preSalePoints}</span>
            &nbsp;
            <span class="text-danger">${points}</span>
            ` :
            points;
        this.customVariantTarget.classList.toggle("d-none", variantId !== "-1");

        if (variantId === "-1") {
            this.updateCustomVariantPoints();
        }
    }

    updateCustomVariantPoints() {
        if (this.customVariantAmountTarget.value) {
            void this.refreshCustomVariantPoints();
        } else {
            this.pointsTarget.innerHTML = this.customAmountTextValue;
        }
    }

    async refreshCustomVariantPoints() {
        if (!this.pointsTarget.querySelector(".spinner-border")) {
            this.pointsTarget.innerHTML = `<div class="spinner-border"></div>`;
        }

        const { price, points, maxPrice } = await this.calculatePointsForCustomVariantAmount();
        this.pointsTarget.innerHTML = points;
        document.getElementById("CustomVariantAmount_Submit").value = price;
        document.getElementById("maxPrice").value = maxPrice;
    }

    async calculatePointsForCustomVariantAmount() {
        const url = new URL(this.calculatePointsForCustomVariantAmountUrlValue, document.location.origin);
        url.searchParams.set("customVariantAmount", this.customVariantAmountTarget.value);
        const response = await fetch(url);
        return await response.json();
    }

    setCustomVariantAmount(e) {
        e.preventDefault();
        this.customVariantAmountTarget.value = e.params.customVariantAmount;
        this.customVariantAmountTarget.focus();
        void this.updateCustomVariantPoints();
    }

    configureGiftFormForRecipientType({ target }) {
        const isSelfRecipientTypeSelected = target.options[target.selectedIndex].value === "self";
        this.giftRecipientTargets.forEach(grt => {
            grt.value = isSelfRecipientTypeSelected && grt.dataset.initialValue || "";
        });
        this.giftOtherTarget.classList.toggle("hide", isSelfRecipientTypeSelected);
    }

    confirmBeforeSubmit(event) {
        event.preventDefault();

        if (!this.isCustomVariantSelected()) {
            this.submitForm();
            return;
        }

        const enteredPrice = document.getElementById("CustomVariantAmount_Submit");
        const maxPrice = document.getElementById("maxPrice");

        if (!enteredPrice || !maxPrice) {
            this.submitForm();
            return;
        }

        const entered = Number(enteredPrice.value);
        const max = Number(maxPrice.value);

        if (Number.isNaN(entered) || Number.isNaN(max)) {
            this.submitForm();
            return;
        }

        // Same value → no dialog needed
        if (entered.toFixed(2) === max.toFixed(2)) {
            this.submitForm();
            return;
        }

        const difference = (max - entered).toFixed(2);

        const message = this.confirmMessageValue
            .replace("{maxPrice}", `${max.toFixed(2)} ${this.currencySuffixValue} (+${difference})`);

        bootbox.dialog({
            title: this.confirmTitleValue,
            message,
            closeButton: true,
            backdrop: true,
            onEscape: () => {
                // Do nothing on escape
            },
            buttons: {
                yes: {
                    label: "Yes",
                    className: "btn-primary",
                    callback: () => {
                        this.submitForm(max);
                    }
                },
                no: {
                    label: "No",
                    className: "btn-light",
                    callback: () => {
                        this.submitForm(entered);
                    }
                }
            }
        });
    }

    isCustomVariantSelected() {
        if (!this.hasVariantSelectTarget) return false;
        return this.variantSelectTarget.value === "-1";
    }

    submitForm(value) {
        const form = this.element.querySelector('form[data-controller="form"]');
        if (!form) return;

        const customAmount = document.getElementById("CustomVariantAmount_Submit");

        if (value && customAmount) customAmount.value = Number(value).toFixed(2);

        form.submit();
    }
}
